import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { tenantsApi } from "@/api/endpoints/tenants";
import { Icon } from "@/components/Icon";
import { EmptyState } from "@/components/EmptyState";

export function AuditLogsPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const query = useQuery({
    queryKey: ["tenants", "current", "audit-logs", { page, action, from, to }],
    queryFn: () =>
      tenantsApi.auditLogs({ page, page_size: 20, action: action || undefined, from: from || undefined, to: to || undefined }),
  });

  const totalPages = Math.max(1, Math.ceil((query.data?.total ?? 0) / 20));

  return (
    <div className="page" data-testid="audit-logs-page">
      <div className="page-head">
        <div>
          <div className="page-title">Audit log</div>
          <div className="page-sub">Every workspace event in the last 90 days.</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input">
          <Icon name="search" size={14} />
          <input
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by action (e.g. tenant.invitation)"
            data-testid="audit-filter-action"
          />
        </div>
        <input
          type="date"
          className="field-input"
          style={{ width: 160 }}
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setPage(1);
          }}
          aria-label="From date"
        />
        <input
          type="date"
          className="field-input"
          style={{ width: 160 }}
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setPage(1);
          }}
          aria-label="To date"
        />
      </div>

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "180px 220px 1fr 1fr" }}
        >
          <div>Time</div>
          <div>Action</div>
          <div>Actor</div>
          <div>Target</div>
        </div>
        {query.data?.items.map((a) => (
          <div
            key={a.id}
            className="list-row"
            style={{ gridTemplateColumns: "180px 220px 1fr 1fr" }}
            data-testid={`audit-row-${a.id}`}
          >
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              {new Date(a.created_at).toLocaleString()}
            </div>
            <div className="audit-action">{a.action}</div>
            <div className="mono" style={{ fontSize: 12 }}>
              {a.actor_email ?? "—"}
            </div>
            <div className="mono" style={{ fontSize: 12, color: "var(--text-3)" }}>
              {a.target_type ? `${a.target_type}:${a.target_id ?? ""}` : "—"}
            </div>
          </div>
        ))}
        {(query.data?.items.length ?? 0) === 0 ? (
          action || from || to ? (
            <div className="empty">No matching events.</div>
          ) : (
            <EmptyState
              icon="history"
              title="No audit events yet"
              description="Every workspace change — invites, role updates, key edits — lands here for the last 90 days."
              testId="empty-audit"
            />
          )
        ) : null}
        <div className="pagination">
          <span>
            Page {page} of {totalPages} · {query.data?.total ?? 0} events
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="btn btn-outline btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              className="btn btn-outline btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
